// ─── IsLogin.jsx ──────────────────────────────────────────────────────────────
import React, { useEffect, useRef, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";

import Spinner from "./Spinner";
import useLogout from "../Logout";

const REFRESH_BEFORE_EXPIRY = 10 * 1000; // refresh 10 s before expiry (not 2 s —
// gives network time to complete before
// the token actually expires)
const IDLE_TIMEOUT = 15 * 60 * 1000;     // 15 minutes

const IsLogin = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector((state) => state.user);
  const logout = useLogout(false); // no success toast on auto-logout / idle logout

  const [loading, setLoading] = useState(true);

  const idleTimer = useRef(null);
  const refreshTimer = useRef(null);
  const refreshAccessTokenRef = useRef(null); // break circular dep (see below)

  // ── Read tokens at call-time, never at render-time ────────────────────────
  const getTokens = useCallback(() => ({
    accessToken: localStorage.getItem("accessToken"),
    refreshToken: localStorage.getItem("refreshToken"),
    csrfToken: localStorage.getItem("csrfToken"),
  }), []);

  // ── scheduleRefresh ───────────────────────────────────────────────────────
  // Calls refreshAccessTokenRef.current so it always invokes the latest
  // version of refreshAccessToken without a circular dependency.
  const scheduleRefresh = useCallback((token) => {
    if (!token) return;
    clearTimeout(refreshTimer.current);

    try {
      const { exp } = jwtDecode(token);
      const delay = exp * 1000 - Date.now() - REFRESH_BEFORE_EXPIRY;

      if (delay <= 0) {
        // Token already expired or expiring imminently — refresh immediately
        refreshAccessTokenRef.current?.();
      } else {
        refreshTimer.current = setTimeout(
          () => refreshAccessTokenRef.current?.(),
          delay
        );
      }
    } catch {
      // Malformed token
      logout();
    }
  }, [logout]);

  // ── refreshAccessToken ────────────────────────────────────────────────────
  // Backend: POST /auth/refresh
  //   Headers : Authorization: Bearer <accessToken>, X-CSRF-Token: <csrfToken>
  //   Body    : { refreshToken }
  //   Returns : { success, data: { accessToken, refreshToken, csrfToken } }
  const refreshAccessToken = useCallback(async () => {
    const { accessToken, refreshToken, csrfToken } = getTokens();

    // Guard: if any token is missing, session is already broken → logout
    if (!accessToken || !refreshToken || !csrfToken) {
      logout();
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/auth/refresh`,
        { refreshToken },          // backend reads from req.body.refreshToken
        {
          headers: {
            Authorization: `Bearer ${accessToken}`, // backend: jwt.verify(accessToken)
            "X-CSRF-Token": csrfToken,               // backend: rotateAccessToken csrfToken lookup
          },
          withCredentials: true,
        }
      );

      const { accessToken: newAccessToken, refreshToken: newRefreshToken, csrfToken: newCsrfToken }
        = res.data?.data ?? {};

      if (!newAccessToken || !newRefreshToken || !newCsrfToken) {
        throw new Error("Incomplete token data in refresh response.");
      }

      // Persist all three — backend rotates all three on every refresh
      localStorage.setItem("accessToken", newAccessToken);
      localStorage.setItem("refreshToken", newRefreshToken);
      localStorage.setItem("csrfToken", newCsrfToken);

      dispatch({ type: "SET_TOKEN", payload: newAccessToken });

      // Schedule next refresh from new token's expiry
      scheduleRefresh(newAccessToken);

    } catch (err) {
      console.error("Token refresh failed:", err?.response?.data?.message || err.message);
      logout();
    }
  }, [getTokens, scheduleRefresh, dispatch, logout]);

  // Keep ref in sync with latest callback (breaks the circular dep)
  useEffect(() => {
    refreshAccessTokenRef.current = refreshAccessToken;
  }, [refreshAccessToken]);

  // ── Idle timer ────────────────────────────────────────────────────────────
  const resetIdleTimer = useCallback(() => {
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(logout, IDLE_TIMEOUT);
  }, [logout]);

  // ── Initial auth check — runs once on mount ───────────────────────────────
  useEffect(() => {
    const { accessToken } = getTokens();

    if (!accessToken) {
      logout();
      return;
    }

    try {
      jwtDecode(accessToken); // throws if malformed
    } catch {
      logout();
      return;
    }

    // Arm refresh timer BEFORE showing children
    scheduleRefresh(accessToken);
    resetIdleTimer();
    setLoading(false);

    return () => {
      clearTimeout(refreshTimer.current);
      clearTimeout(idleTimer.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset idle timer on every route change
  useEffect(() => {
    resetIdleTimer();
  }, [location.pathname, resetIdleTimer]);

  // Activity listeners reset the idle timer
  useEffect(() => {
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetIdleTimer, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
      clearTimeout(idleTimer.current);
    };
  }, [resetIdleTimer]);

  // Reactive logout if server marks user inactive
  useEffect(() => {
    if (user?.status === 0) logout();
  }, [user?.status, logout]);

  if (loading) return <Spinner />;
  return <>{children}</>;
};

export default IsLogin;